import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import camelize from 'camelize';
import debounce from 'lodash/debounce';

import {
  IOutlookComponentSystem,
  IOutlookSystem,
  IRenderedComponent,
} from '@base/outlookModel';
import { DOMAIN } from '@constants';
import {
  ChatieFile,
  IChat,
  IChatCharacter,
  IChatContent,
  IChatDetail,
  IChatMessageImageDescription,
  IChatPagination,
  IChatProfile,
  IChatPurchaseMessage,
  IChatResponse,
  IChatRoom,
  IChatRoomChemiStatus,
  IChatRoomInfo,
  IChatSituationDetail,
  IChatSituationInfo,
  IChemiReport,
  IDisplayPreset,
  IEFinderObject,
  IEditMessage,
  IEpisodeDraft,
  IMyMessageToken,
  IPagination,
  IPeekRoomInfo,
  IPlayer,
  IRecommendMessage,
  IRecommendedPlots,
  IRecommendedPlotsPromotion,
  IStory,
  IStoryChatRoom,
  IViralContentDetail,
} from '@model';
import { toQueryString } from '@utils/misc';

import { api, transformRequest } from '.';

const chatBotAPIConfig: AxiosRequestConfig = {
  baseURL: process.env.NEXT_PUBLIC_CHAT_BOT_API_URL,
  responseType: 'json',
  withCredentials: true,
  headers: {
    accept: '*/*',
  },
  transformRequest,
};

export const CHAT_BOT_API = axios.create(chatBotAPIConfig);

CHAT_BOT_API.interceptors.request.use((config) => {
  if (!config.headers) {
    config.headers = {};
  }

  if (typeof window !== 'undefined') {
    const CHEMING_DOMAIN = process.env.NEXT_PUBLIC_CHEMING_DOMAIN;
    const currentHost = window.location.host;
    const chemingHost = CHEMING_DOMAIN ? new URL(CHEMING_DOMAIN).host : '';
    const isCheming = currentHost === chemingHost;

    config.headers['X-SERVICE-ID'] = isCheming ? DOMAIN.cheming : DOMAIN.chatie;
  }

  return config;
});

export const getAICharacter = async (storyId: number) => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IChatProfile[]>>(
    `/characters/${toQueryString({ status: 'PUB', storyId })}`
  );
  return data.result;
};

export const getAICharacterProfile = async (characterId?: number) => {
  if (characterId) {
    try {
      const { data } = await CHAT_BOT_API.get<IChatResponse<IChatProfile[]>>(
        `/characters/${characterId}/profiles/`
      );
      return data.result;
    } catch (err) {}
  }
};

export interface PostAIChatRoomPayload {
  user_nickname: string;
  situation_id: number;
  character_id?: number;
  profile_id?: number;
  is_sexual?: boolean;
  llm_preset_code?: string | undefined;
  initial_messages?: {
    content: string;
    role: 'human' | 'ai';
  }[];
}

export const postAIChatRoom = async (payload: PostAIChatRoomPayload) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IChatRoomInfo>>(
    `/chat-rooms/`,
    payload
  );
  return data.result;
};

export const postAIChatMessage = async (
  chatRoomId: string,
  content: string,
  presetId: number | undefined,
  chapterId: number | null,
  imageDescriptionId?: number | null
) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IChatContent>>(
    `/v2/chat-rooms/${chatRoomId}/messages/`,
    {
      content,
      display_preset_id: presetId,
      chapter_id: chapterId,
      ...(imageDescriptionId && { image_description_id: imageDescriptionId }),
    }
  );

  // if (!!data?.result?.chemi_status_histories?.length) {
  //   data.result.chemi_status_histories.map((item) => {
  //     const prev = item.previous_value;
  //     const applied = item.applied_value;
  //     const newDelta = applied - prev;
  //     return {
  //       ...item,
  //       delta: newDelta,
  //     };
  //   });
  // }

  return data.result;
};

export const getAIChatSituationDetail = async (situationId: number | null) => {
  try {
    const { data } = await CHAT_BOT_API.get<
      IChatResponse<IChatSituationDetail>
    >(`situations/${situationId}/`);

    return data.result;
  } catch (err) {
    console.log(err);
  }
};

export const postAIChatPurchaseMessage = async (
  chatRoomId: number,
  type: 'create' | 'update'
) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IChatPurchaseMessage>>(
    `chat-rooms/${chatRoomId}/messages/purchase`,
    {
      type,
    }
  );

  return data.result;
};

export const postChemiReportShare = async (chatRoomId: number) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IChatPurchaseMessage>>(
    `chat-rooms/${chatRoomId}/chemi-report/share-enablement/`
  );

  return data.result;
};

export const patchAIChatMessage = async ({
  chatRoomId,
  chatId,
  content,
  presetId,
}: {
  chatRoomId: number;
  chatId: string;
  content: string;
  presetId: number | undefined;
}) => {
  const { data } = await CHAT_BOT_API.patch<IChatResponse<IChatContent>>(
    `chat-rooms/${chatRoomId}/messages/${chatId}/refresh/`,
    {
      content,
      display_preset_id: presetId,
    }
  );

  return data;
};

export interface IChatbotInfo {
  situation_id: number;
  situation_group_id: number;
  group_key: string;
  character: IChatProfile;
  profile: IChatProfile;
}

export type ChatRelationsType = 'story' | 'episode' | 'character';

export const fetchPreviousChat = async ({
  chatRoomId,
  cursor,
  per_page,
}: {
  chatRoomId: number | string | undefined;
  cursor?: string | number;
  per_page?: number;
}) => {
  if (!chatRoomId) {
    alert('잠시 후 다시 시도해주세요');
    return;
  }

  const { data } = await CHAT_BOT_API.get<IChatResponse<IChatDetail>>(
    `/v2/chat-rooms/${chatRoomId}/messages/${toQueryString({
      cursor,
      per_page,
    })}`
  );

  // const { data: data } = await CHAT_BOT_API.get<
  //   IChatResponse<IChatPagination<IOmniMessage>>
  // >(
  //   `chat-rooms/${chatRoomId}/omni-messages/${toQueryString({
  //     cursor,
  //     per_page,
  //   })}`
  // );
  const paging = data?.result;
  // // return paging;
  // const items = paging?.items.map((item) => {
  //   if (item.type === OmniType.text) {
  //     return item.message;
  //   } else if (item.type === OmniType.visual_selfie) {
  //     return camelize(item.message);
  //   } else if (item.type === OmniType.visual_selfie_request) {
  //     return camelize(item.message);
  //   }
  // });
  // const chatDetail = {
  //   ...paging,
  //   items: items?.filter((a) => !!a),
  // };

  return paging;
};

export const fetchChatRoomDetail = async (chatRoomId: number) => {
  if (!chatRoomId) {
    alert('잠시 후 다시 시도해주세요');
    return;
  }

  const { data } = await CHAT_BOT_API.get<IChatResponse<IChatRoomInfo>>(
    `chat-rooms/${chatRoomId}/`
  );

  return data?.result;
};

export type ChatRoomStatus = 'AT' | 'IAT';

export type ChatRoomsQueryType = {
  cursor?: string;
  is_extend_chat?: boolean;
  last_chat_in_days?: number;
  ordering?: string;
  status?: ChatRoomStatus;
  visualization_level?: 'PUB' | 'PRV' | 'HIDE' | 'ADM';
  page?: number;
  perPage?: number;
  character_id?: number;
};

export type CharacterInfoQueryType = {
  situation_group_id?: number;
  status?: ChatRoomStatus;
  visualization_level?: 'PUB' | 'PRV' | 'HIDE' | 'ADM';
  page?: number;
  perPage?: number;
  sort?: 'desc' | 'asc';
  character_id?: number;
};
export type CharacterOrderType = 'stat__message_count' | 'id' | 'like_at';

export type MyCharacterQueryType = {
  page?: number;
  perPage?: number;
  sort?: 'desc' | 'asc';
  orderBy: CharacterOrderType;
  storyId?: number;
};

export type OutlookPage =
  | 'home'
  | 'library-play'
  | 'home_ground'
  | 'home_peek'
  | 'home_peek__all';

export const fetchChatRooms = async (
  query?: ChatRoomsQueryType,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IChatRoom>>
  >(
    `/chat-rooms/${toQueryString({
      ...query,
      page,
      perPage,
    })}`
  );

  return data?.result;
};

const _populate = (items: IStoryChatRoom[], objects: IStory[]) => {
  return items.map((item) => {
    const object = objects.find((s) => s.id === item.episode?.story_id);
    if (object) {
      return {
        ...item,
        story: object,
      } as IStoryChatRoom;
    }

    return item;
  });
};

export const fetchStoryChatRooms = async (page = 1, perPage = 20) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IStoryChatRoom>>
  >(
    `/episode-chat-rooms/${toQueryString({
      page,
      perPage,
    })}`
  );

  const storyIds = data.result?.items.map((item) => item?.episode?.story_id);

  if (storyIds.length > 0) {
    const { data: stories } = await api.get<IStory[]>(
      `/stories/${toQueryString({ storyIds: storyIds.join(',') })}`
    );
    data.result.items = _populate(data.result?.items, stories);
  }

  return data?.result;
};

export const updateChatRoomsState = async ({
  chat_room_ids,
  status,
  visualization_level,
}: {
  chat_room_ids: number[];
  status?: ChatRoomStatus;
  visualization_level?: 'PUB' | 'PRV' | 'HIDE' | 'ADM';
}) => {
  const { data } = await CHAT_BOT_API.patch<IChatResponse<IChatRoomInfo>>(
    `/chat-rooms/update-selected/`,
    {
      chat_room_ids,
      status,
      visualization_level,
    }
  );

  return data?.result;
};

export const getOutlookComponent = async (key: string) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IOutlookComponentSystem>
  >(`/components/${key}`);

  return data?.result;
};

export const getOutlookComponentRenderItem = async <T>(
  key: string,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IChatPagination<T>>>(
    `/components/${key}/rendered-items${toQueryString({
      page,
      perPage,
    })}`
  );

  return data?.result;
};

export const toggleCharacterLike = async (characterId: number) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IOutlookSystem>>(
    `/characters/${characterId}/like/?action=toggle`
  );

  return data?.result;
};

export const getCharacterDetail = async (characterId?: number) => {
  try {
    if (!characterId) {
      return alert('캐릭터가 없습니다.');
    }

    const { data } = await CHAT_BOT_API.get<IChatResponse<IChatCharacter>>(
      `/characters/${characterId}/`
    );

    return data?.result;
  } catch (err) {
    return undefined;
  }
};

export const fetchSituations = async (
  query?: CharacterInfoQueryType,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IChatSituationInfo>>
  >(
    `/situations/${toQueryString({
      ...query,
      page,
      perPage,
    })}`
  );

  return data?.result;
};

export type CharacterQueryParams = {
  only_like?: boolean;
  order_by?: string;
  sort?: 'asc' | 'desc';
  status?: 'PUB' | 'PRV' | 'DRF';
  story_id?: number;
  user_id?: number;
  ids?: string[];
};

export const fetchCharacters = async (
  query: CharacterQueryParams,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IChatCharacter>>
  >(
    `/characters/${toQueryString({
      ...query,
      page,
      perPage,
    })}`
  );
  return data?.result;
};

export const getPaginationOutlookPage = async (
  pageName: OutlookPage,
  page = 1,
  perPage = 20,
  headers?: Record<string, string>
) => {
  const query = {
    visualization_level: 'PUB',
  };

  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IRenderedComponent>>
  >(
    `/outlooks/${pageName}/rendered-components/${toQueryString({
      ...query,
      page,
      perPage: perPage,
    })}`,
    {
      headers: {
        ...headers,
      },
    }
  );

  return data?.result;
};

export const fetchPlayer = async (id: number | undefined) => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IPlayer>>(
    `/players/${id}/`
  );

  return data?.result;
};

export const fetchMyPlayerStats = async () => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IPlayer>>(
    `/players/me/stats/`
  );

  return data?.result;
};

export const deletePlaySet = async (characterId: number) => {
  const { data } = await CHAT_BOT_API.delete(`playset/${characterId}/`);

  return data?.result;
};

export const fetchMyCharacters = async (
  query: MyCharacterQueryType,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IChatCharacter>>
  >(
    `/my-characters/${toQueryString({
      ...query,
      page,
      perPage,
    })}`
  );

  return data?.result;
};

export const fetchDisplayPresets = async (group_key: string | undefined) => {
  try {
    const { data } = await CHAT_BOT_API.get<IChatResponse<IDisplayPreset[]>>(
      `/display-presets/${toQueryString({
        group_key,
      })}`
    );

    return data?.result;
  } catch (e: unknown) {
    const err = e as AxiosError;
    if (err?.response?.status === 401) {
      return [];
    }
    throw e;
  }
};

export const fetchDisplayPresetDetail = async (
  presetId: string | undefined | null
) => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IDisplayPreset>>(
    `/display-presets/${presetId}`
  );

  return data?.result;
};

export type IUpdatePreset = {
  code: string;
};

export const updatePreset = async (code: string) => {
  try {
    const { data } = await CHAT_BOT_API.patch<IChatResponse<IUpdatePreset>>(
      `/players/me/display-preset/`,
      { code }
    );
    return data?.result;
  } catch (e) {
    const err = e as AxiosError;
    if (err?.response?.status === 429) {
      alert('요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.');
      return undefined;
    }
    throw e;
  }
};
export const bulkDeleteMessages = async ({
  chatRoomId,
  messageIds,
}: {
  chatRoomId: string | number;
  messageIds: string[];
}) => {
  const { data } = await CHAT_BOT_API.delete<IChatResponse<null>>(
    `/chat-rooms/${chatRoomId}/messages/bulk/${toQueryString({
      chat_message_ids: messageIds,
    })}`
  );

  return data?.result;
};

export const editAIChatMessage = async ({
  messageId,
  chatRoomId,
  content,
}: {
  messageId: string | number;
  chatRoomId: string | number;
  content: string;
}) => {
  const { data } = await CHAT_BOT_API.patch<IChatResponse<IEditMessage>>(
    `chat-rooms/${chatRoomId}/messages/${messageId}/`,
    {
      content,
    }
  );

  return data?.result;
};

export const fetchRecommendSituations = async (
  situationId: number | string,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IPagination<IChatSituationDetail>>
  >(
    `/situations/${situationId}/recommendations/${toQueryString({
      page,
      perPage,
    })}`
  );

  return data?.result;
};

export const trackRecommendSituations = async ({
  situation_id,
  event_type,
  session_id,
}: {
  situation_id: number;
  session_id: string;
  event_type: 'View';
  event_value?: string;
}) => {
  await CHAT_BOT_API.post<IChatResponse<IPagination<IChatSituationDetail>>>(
    `/situations/${situation_id}/recommendations-tracking/`,
    {
      event_type,
      session_id,
    }
  );
};

export const fetchChemiStatus = async (chatRoomId?: number) => {
  if (chatRoomId) {
    try {
      const { data } = await CHAT_BOT_API.get<
        IChatResponse<IChatRoomChemiStatus>
      >(`/chat-rooms/${chatRoomId}/chemi-status/`);
      return data?.result;
    } catch (err) {}
  }
};

export type FetchPeekRoomsQuery = {
  character_id?: number;
  only_my_character?: boolean;
  order_by?: 'id' | 'peek__tap_count' | 'random';
  sort?: 'asc' | 'desc';
  story_id?: number;
  with_last_chat_message?: boolean;
};

export const fetchPeekRooms = async (
  query: FetchPeekRoomsQuery | undefined,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IPeekRoomInfo>>
  >(
    `/chat-room-peeks/${toQueryString({
      ...query,
      page,
      perPage,
    })}`
  );

  return data?.result;
};

export const togglePeek = async ({
  id,
  hooking_ment,
  is_anonymous,
}: {
  id: number | string;
  hooking_ment?: string;
  is_anonymous?: boolean;
}) => {
  const { data } = await CHAT_BOT_API.post(`/chat-room-peeks/${id}/toggle/`, {
    hooking_ment,
    is_anonymous,
  });

  return data?.result;
};

export const patchPeek = async ({
  id,
  hooking_ment,
}: {
  id: number | string;
  hooking_ment?: string;
}) => {
  const { data } = await CHAT_BOT_API.patch(`/chat-room-peeks/${id}/`, {
    hooking_ment,
  });

  return data?.result;
};

export const fetchPeekRoomDetail = async (chatRoomId: number | string) => {
  if (!chatRoomId) {
    alert('잠시 후 다시 시도해주세요');
    return;
  }

  const { data } = await CHAT_BOT_API.get<IChatResponse<IPeekRoomInfo>>(
    `/chat-room-peeks/${chatRoomId}/`
  );

  return data?.result;
};

export const fetchPeekRoomMessages = async (chatRoomId: number | string) => {
  if (!chatRoomId) {
    alert('잠시 후 다시 시도해주세요');
    return;
  }

  const { data } = await CHAT_BOT_API.get<IChatResponse<IChat[]>>(
    `/chat-room-peeks/${chatRoomId}/messages/`
  );

  return data?.result;
};

export const postPeekRoomRead = async ({
  chatRoomId,
  tapCount,
}: {
  chatRoomId: number | string;
  tapCount: number;
}) => {
  if (!chatRoomId) {
    alert('잠시 후 다시 시도해주세요');
    return;
  }
  try {
    const { data } = await CHAT_BOT_API.post(
      `/chat-room-peeks/${chatRoomId}/read/`,
      {
        tap_count: tapCount,
      }
    );
    return data?.result;
  } catch (err) {
    return;
  }
};

export const fetchMyMessageToken = async () => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IMyMessageToken>>(
    `/players/me/message-token`
  );
  return data?.result;
};

type createRecommendedPlotsProps = {
  episodeJson?: ChatieFile | IEpisodeDraft;
  conversationSummary?: string;
};

export const createRecommendedPlots = async ({
  episodeJson,
  conversationSummary,
}: createRecommendedPlotsProps) => {
  const { data } = await CHAT_BOT_API.post<IChatResponse<IRecommendedPlots>>(
    `/studio-helper/plot-branches/`,
    {
      conversationSummary,
      episodeJson,
    }
  );

  return camelize(data?.result);
};

export const fetchPlotRecommendPromotionInfo = async () => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IRecommendedPlotsPromotion>
  >(`/studio-helper/plot-branches/promotion-info/`);

  return camelize(data?.result);
};

export const fetchPlotDevelopmentPromotionInfo = async () => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IRecommendedPlotsPromotion>
  >(`/studio-helper/plot-developments/promotion-info/`);

  return camelize(data?.result);
};

type createPlotDevelopmentProps = {
  episodeJson: ChatieFile | IEpisodeDraft;
  direction?: string;
};

export const createPlotDevelopments = async ({
  episodeJson,
  direction,
}: createPlotDevelopmentProps) => {
  const payload: {
    episodeJson: ChatieFile | IEpisodeDraft;
    direction?: string;
  } = { episodeJson };

  if (direction) {
    payload.direction = direction;
  }
  const { data } = await CHAT_BOT_API.post<
    IChatResponse<IRecommendedPlotsPromotion>
  >(`/studio-helper/plot-developments/`, payload);

  return camelize(data?.result);
};

type RecommendMessageParams = {
  chatRoomId?: number;
  situationId?: number;
  presetCode?: string;
};

const fetchRecommendMessagesInternal = async ({
  chatRoomId,
  situationId,
  presetCode,
}: RecommendMessageParams): Promise<IRecommendMessage[]> => {
  const { data } = await CHAT_BOT_API.get<IChatResponse<IRecommendMessage[]>>(
    `/chat-message-recommendation/${toQueryString({
      chatRoomId,
      situationId,
      presetCode,
    })}`
  );

  return data?.result;
};

export const fetchRecommendMessages = (() => {
  const pendingRequests = new Map<string, Promise<IRecommendMessage[]>>();

  const debouncedFunction = debounce(
    async (
      key: string,
      params: RecommendMessageParams,
      // eslint-disable-next-line @typescript-eslint/ban-types
      resolve: Function,
      // eslint-disable-next-line @typescript-eslint/ban-types
      reject: Function
    ) => {
      try {
        const result = await fetchRecommendMessagesInternal(params);
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        pendingRequests.delete(key);
      }
    },
    1000
  );

  return async (
    params: RecommendMessageParams
  ): Promise<IRecommendMessage[]> => {
    const key = JSON.stringify(params);
    if (pendingRequests.has(key)) {
      return pendingRequests.get(key)!;
    }

    const newPromise = new Promise<IRecommendMessage[]>((resolve, reject) => {
      debouncedFunction(key, params, resolve, reject);
    });

    pendingRequests.set(key, newPromise);
    return newPromise;
  };
})();

export const postHiddenFeeling = async ({
  chatRoomId,
}: {
  chatRoomId: number | string;
}) => {
  try {
    if (!chatRoomId) {
      alert('잠시 후 다시 시도해주세요');
      return;
    }
    const { data } = await CHAT_BOT_API.post<{ result: { message: string } }>(
      `/chat-rooms/${chatRoomId}/hidden-feeling/`
    );
    return data?.result;
  } catch (err) {
    return undefined;
  }
};

export const fetchChemiReport = async ({
  chatRoomId,
}: {
  chatRoomId: number | string;
}) => {
  if (!chatRoomId) {
    alert('존재하지 않은 채팅방입니다.');
    return;
  }
  const { data } = await CHAT_BOT_API.post<IChatResponse<IChemiReport>>(
    `/chat-rooms/${chatRoomId}/chemi-report/`
  );
  return data?.result;
};

export const postChatMessageImageDescriptions = async ({
  chatRoomId,
  image,
  context,
}: {
  chatRoomId: number | string;
  image: IEFinderObject<'image'>;
  context?: string;
}) => {
  if (!chatRoomId) {
    alert('존재하지 않은 채팅방입니다.');
    return;
  }
  const { data } = await CHAT_BOT_API.post<
    IChatResponse<IChatMessageImageDescription>
  >(`/chat-rooms/${chatRoomId}/chat-message-image-descriptions/`, {
    image,
    ...(context !== '' && { context }),
  });
  return data?.result;
};

export const fetchViralContents = async (uuid: string) => {
  if (!uuid) {
    alert('잠시 후 다시 시도해주세요');
    return;
  }

  const { data } = await CHAT_BOT_API.get<IChatResponse<IViralContentDetail>>(
    `/viral-contents/${uuid}/`
  );

  return data?.result;
};
